// Recebimentos do título + acesso ao PAGAMENTO AGRUPADO (1 payment -> varias allocations)
import React, { useCallback, useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Loader2, Layers, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const brl = (v) => (Number(v || 0)).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const brDate = (d) => (d ? new Date(`${String(d).slice(0, 10)}T12:00:00`).toLocaleDateString('pt-BR') : '-');

export default function TituloRecebimentos({ titleId, onOpenChange, onChanged }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [group, setGroup] = useState(null);
  const [motivo, setMotivo] = useState('');

  const load = useCallback(async () => {
    if (!titleId) { setData(null); return; }
    try {
      const r = await api.get(`/financial/titulos/${titleId}/payments`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao carregar os recebimentos.');
    }
  }, [titleId]);

  useEffect(() => { load(); }, [load]);

  const abrirGrupo = async (pid) => {
    setBusy(true);
    try {
      const r = await api.get(`/financial/titulos/pagamentos-agrupados/${pid}`);
      setGroup(r.data.payment);
      setMotivo('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Pagamento agrupado nao encontrado.');
    } finally { setBusy(false); }
  };

  const estornarGrupo = async () => {
    if (!motivo.trim()) { toast.error('Informe o motivo do estorno.'); return; }
    setBusy(true);
    try {
      const r = await api.post(
        `/financial/titulos/pagamentos-agrupados/${group.payment_id}/estornar`, { reason: motivo });
      toast.success(`Pagamento estornado em ${r.data.count} titulo(s).`);
      setGroup(null);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Falha ao estornar o pagamento.');
    } finally { setBusy(false); }
  };

  const linha = (p, estornado) => (
    <div key={p.payment_id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-[12.5px]"
      data-testid={`recebimento-${p.payment_id}`}>
      <div className="min-w-0">
        <p className="font-semibold text-slate-800 truncate">
          {brl(p.gross_amount ?? p.amount)} · {p.method_label || p.method} {p.installments > 1 ? `· ${p.installments}x` : ''}
        </p>
        <p className="text-[11px] text-slate-500 truncate">
          {brDate(p.paid_at)} · {p.account_name || 'sem conta'} · {p.payment_id.slice(0, 8)}
          {p.registered_by ? ` · por ${p.registered_by}` : ''}
        </p>
      </div>
      <div className="flex items-center gap-2">
        {estornado && <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200">Estornado</Badge>}
        {p.grouped && (
          <Button size="sm" variant="outline" className="h-7 text-[11px]" disabled={busy}
            data-testid={`abrir-agrupado-${p.payment_id}`}
            onClick={() => abrirGrupo(p.group_payment_id || p.payment_id)}>
            <Layers className="w-3 h-3 mr-1" /> Pagamento agrupado
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <>
      <Dialog open={!!titleId && !group} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-2xl" data-testid="titulo-recebimentos">
          <DialogHeader>
            <DialogTitle>Recebimentos do titulo</DialogTitle>
            <DialogDescription>
              {data ? `${data.status.toUpperCase()} · recebido ${brl(data.received)} de ${brl(data.amount_due)} · saldo ${brl(data.balance)}` : 'Carregando...'}
            </DialogDescription>
          </DialogHeader>

          {!data ? (
            <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin" /></div>
          ) : (
            <div className="space-y-3">
              <div className="rounded-lg border divide-y" data-testid="recebimentos-lista">
                {(data.payments || []).length === 0 && (
                  <p className="p-3 text-[12.5px] text-slate-500">Nenhum recebimento registrado.</p>
                )}
                {(data.payments || []).map((p) => linha(p, false))}
              </div>
              {!!(data.reversed || []).length && (
                <>
                  <p className="text-[11.5px] font-semibold text-slate-500">Estornados (historico preservado)</p>
                  <div className="rounded-lg border divide-y opacity-70">
                    {(data.reversed || []).map((p) => linha(p, true))}
                  </div>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!group} onOpenChange={(v) => { if (!v) setGroup(null); }}>
        <DialogContent className="sm:max-w-2xl" data-testid="pagamento-agrupado">
          <DialogHeader>
            <DialogTitle>Pagamento agrupado</DialogTitle>
            <DialogDescription>
              {group ? `${brDate(group.paid_at)} · ${group.method_label} · ${group.account_name || 'sem conta'} · ${group.count} titulo(s)` : ''}
            </DialogDescription>
          </DialogHeader>

          {!!group && (
            <div className="space-y-3 text-[12.5px]">
              <div className="rounded-lg border divide-y" data-testid="allocations-lista">
                {(group.allocations || []).map((a) => (
                  <div key={a.title_id} className="flex items-center justify-between gap-2 px-3 py-2">
                    <span className="truncate">
                      {a.student_name} · {a.description}
                      <em className="not-italic text-slate-500"> · venc. {brDate(a.due_date)}</em>
                    </span>
                    <span className="whitespace-nowrap">
                      <b>{brl(a.allocated_amount)}</b>
                      {a.partial && <em className="not-italic text-amber-700"> · parcial (saldo {brl(a.balance_after)})</em>}
                    </span>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-3 gap-2 rounded-lg bg-slate-50 p-3">
                <p>Bruto<br /><b>{brl(group.gross_amount)}</b></p>
                <p>Taxa<br /><b>{brl(group.fee_amount)}</b></p>
                <p>Liquido<br /><b>{brl(group.net_amount)}</b></p>
              </div>

              <Button variant="outline" className="w-full" data-testid="btn-recibo-agrupado"
                onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/financial/recibo-lote/${group.payment_id}/recibo.jpg`, '_blank')}>
                Recibo unico (JPEG/PDF)
              </Button>

              {group.reversed ? (
                <p className="rounded-lg bg-rose-50 border border-rose-200 p-3 text-rose-800">
                  Pagamento estornado em {brDate(group.reversed_at)} · {group.reversal_reason}
                </p>
              ) : (
                <div className="flex flex-wrap items-center gap-2">
                  <Input value={motivo} onChange={(e) => setMotivo(e.target.value)}
                    placeholder="Motivo do estorno (obrigatorio)" className="h-9 flex-1 min-w-[180px]"
                    data-testid="motivo-estorno-agrupado" />
                  <Button variant="destructive" disabled={busy} onClick={estornarGrupo}
                    data-testid="btn-estornar-agrupado">
                    <RotateCcw className="w-4 h-4 mr-2" /> Estornar pagamento inteiro
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
